<div class="container mx-auto px-4 py-8 max-w-7xl">
  <div class="mb-6">
    <h1 class="text-3xl font-bold text-gray-900 mb-2">Standaufsichten und Schießleitung</h1>
    <p class="text-gray-600">Veranstaltungskalender</p>
  </div>

  <!-- Loading State -->
  @if (isLoading()) {
    <div class="text-center py-8">
      <p class="text-slate-600">Lade Standaufsichten und Schießleitung...</p>
    </div>
  }

  <!-- Error State -->
  @if (error()) {
    <div class="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 mb-6">
      {{ error() }}
    </div>
  }

  <!-- Search -->
  @if (!isLoading() && !error()) {
    <div class="mb-6">
      <div class="relative">
        <input
          type="text"
          [value]="searchTerm()"
          (input)="onSearchChange($any($event.target).value)"
          placeholder="Suchen..."
          class="w-full px-4 py-3 pr-20 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
        />
        @if (searchTerm()) {
          <button
            (click)="clearSearch()"
            class="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 text-sm text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        }
      </div>
      <p class="mt-2 text-sm text-gray-500">
        {{ filteredSchedules().length }} von {{ schedules().length }} Einträgen
      </p>
    </div>

  <!-- Desktop Table -->
  <div class="hidden lg:block overflow-x-auto shadow-md rounded-lg">
    <table class="w-full bg-white">
      <thead class="bg-gray-50 border-b-2 border-gray-200">
      <tr>
        <th
          (click)="onSort('anlage')"
          class="px-6 py-4 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
          [class.bg-blue-50]="isSortedColumn('anlage')"
        >
          <div class="flex items-center gap-2">
            <span>Anlage</span>
            <span class="text-gray-400">{{ getSortIcon('anlage') }}</span>
          </div>
        </th>
        <th
          (click)="onSort('stand')"
          class="px-6 py-4 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
          [class.bg-blue-50]="isSortedColumn('stand')"
        >
          <div class="flex items-center gap-2">
            <span>Stand</span>
            <span class="text-gray-400">{{ getSortIcon('stand') }}</span>
          </div>
        </th>
        <th
          (click)="onSort('datum')"
          class="px-6 py-4 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
          [class.bg-blue-50]="isSortedColumn('datum')"
        >
          <div class="flex items-center gap-2">
            <span>Datum</span>
            <span class="text-gray-400">{{ getSortIcon('datum') }}</span>
          </div>
        </th>
        <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700">
          Anwesenheit
        </th>
        <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700">
          Anzahl Personen / Verein
        </th>
        <th class="px-6 py-4 text-left text-sm font-semibold text-gray-700">
          Leitung
        </th>
      </tr>
      </thead>
      <tbody class="divide-y divide-gray-200">
        @for (schedule of filteredSchedules(); track schedule.datum) {
          <tr class="hover:bg-gray-50">
            <td class="px-6 py-4 text-sm text-gray-900">{{ schedule.anlage }}</td>
            <td class="px-6 py-4 text-sm text-gray-900">{{ schedule.stand }}</td>
            <td class="px-6 py-4 text-sm text-gray-900">{{ formatDate(schedule.datum) }}</td>
            <td class="px-6 py-4 text-sm text-gray-900">
              <div>{{ schedule.anwesenheit.von }} – {{ schedule.anwesenheit.bis }}</div>
              @if (schedule.anwesenheit.wechsel) {
                <div class="text-xs text-gray-500 mt-1">Wechsel: {{ schedule.anwesenheit.wechsel }}</div>
              }
            </td>
            <td class="px-6 py-4 text-sm text-gray-900">
              @for (pv of schedule.personenVerein; track $index) {
                <div class="mb-1">
                  <span class="font-medium">{{ pv.anzahl }}x {{ pv.name }}</span>
                  @if (pv.anmerkung) {
                    <span class="text-gray-500 text-xs ml-1">({{ pv.anmerkung }})</span>
                  }
                </div>
              }
            </td>
            <td class="px-6 py-4 text-sm text-gray-900">
              @if (schedule.leitung && schedule.leitung.length > 0) {
                @for (leiter of schedule.leitung; track leiter) {
                  <div>{{ leiter }}</div>
                }
              } @else {
                <span class="text-gray-400">-</span>
              }
            </td>
          </tr>
        }
      </tbody>
    </table>

    @if (filteredSchedules().length === 0) {
      <div class="bg-white p-12 text-center">
        <p class="text-gray-500">Keine Ergebnisse gefunden</p>
      </div>
    }
  </div>

  <!-- Mobile Cards -->
  <div class="lg:hidden space-y-4">
    @for (schedule of filteredSchedules(); track schedule.datum) {
      <div class="bg-white rounded-lg shadow-md p-4 border border-gray-200">
        <div class="flex justify-between items-start mb-3">
          <div>
            <h3 class="font-bold text-lg text-gray-900">{{ schedule.anlage }}</h3>
            <p class="text-sm text-gray-600">{{ schedule.stand }}</p>
          </div>
          @if (schedule.leitung && schedule.leitung.length > 0) {
            <span class="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-1 rounded">
              {{ schedule.leitung.join(', ') }}
            </span>
          }
        </div>

        <div class="space-y-2 text-sm">
          <div class="text-gray-700">
            <span class="font-medium">{{ formatDate(schedule.datum) }}</span>
          </div>
          <div class="text-gray-700">
            {{ schedule.anwesenheit.von }} – {{ schedule.anwesenheit.bis }}
            @if (schedule.anwesenheit.wechsel) {
              <div class="text-xs text-gray-500 mt-1">Wechsel: {{ schedule.anwesenheit.wechsel }}</div>
            }
          </div>
          <div class="mt-3 pt-3 border-t border-gray-200">
            @for (pv of schedule.personenVerein; track $index) {
              <div class="text-gray-900 mb-1">
                • <span class="font-medium">{{ pv.anzahl }}x {{ pv.name }}</span>
                @if (pv.anmerkung) {
                  <span class="text-gray-500 text-xs">({{ pv.anmerkung }})</span>
                }
              </div>
            }
          </div>
        </div>
      </div>
    }

    @if (filteredSchedules().length === 0) {
      <div class="bg-white rounded-lg shadow-md p-8 text-center">
        <p class="text-gray-500">Keine Ergebnisse</p>
      </div>
    }
  </div>
  }
</div>
